import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {CarouselV2} from '@pepsico-ds/ui';
import Tab from '@/components/atoms/Tab/Tab';
import TabsNavigation from '@/components/atoms/TabsNavigation/TabsNavigation';
import MediaPreview from './MediaPreview';
import {
  DEFAULT_MEDIA_PREVIEW_TYPE,
  MEDIA_PREVIEW_CONFIG
} from './mediaPreviewConfig';

const DEFAULT_TYPES = [DEFAULT_MEDIA_PREVIEW_TYPE];

/**
 * Removes duplicate or unsupported preview types and guarantees a valid fallback.
 * Numeric and string representations of the same type are treated as duplicates.
 */
const normalizeTypes = (types) => {
  const supportedTypes = Array.isArray(types)
    ? types.filter(
        (type, index) =>
          MEDIA_PREVIEW_CONFIG[type] &&
          types.findIndex((candidate) => String(candidate) === String(type)) ===
            index
      )
    : [];

  return supportedTypes.length ? supportedTypes : [DEFAULT_MEDIA_PREVIEW_TYPE];
};

/**
 * Keeps valid content objects and returns an empty item when no content exists.
 * The empty item allows MediaPreview to display its built-in placeholder.
 */
const normalizeContent = (content) => {
  const validContent = Array.isArray(content)
    ? content.filter(
        (item) => item && typeof item === 'object' && !Array.isArray(item)
      )
    : [];

  return validContent.length ? validContent : [{}];
};

/**
 * Renders media content in one or more preview layouts.
 * Tabs select the preview layout, while CarouselV2 navigates content items.
 * Both controls are omitted when their corresponding arrays contain one item.
 *
 * @param {Object} props
 * @param {(number|string)[]} props.types - Supported preview layout IDs.
 * @param {Object[]} props.content - MediaPreview data for each carousel slide.
 * @param {number} [props.scale] - Default scale applied to every preview.
 * @param {number|string} [props.borderRadius] - Default overlay border radius.
 * @param {Function} [props.renderContentMeta] - Renders metadata below a slide.
 * @param {string} [props.className] - Additional wrapper classes.
 * @returns {JSX.Element}
 */
function MediaPreviewWrapper({
  types = DEFAULT_TYPES,
  content = [],
  scale,
  borderRadius,
  renderContentMeta,
  className = ''
}) {
  const normalizedTypes = useMemo(() => normalizeTypes(types), [types]);
  const normalizedContent = useMemo(() => normalizeContent(content), [content]);
  const [activeTypeIndex, setActiveTypeIndex] = useState(0);

  // Reset an out-of-range selection when the available preview types change.
  useEffect(() => {
    setActiveTypeIndex((currentIndex) =>
      currentIndex < normalizedTypes.length ? currentIndex : 0
    );
  }, [normalizedTypes]);

  const activeType = normalizedTypes[activeTypeIndex] ?? normalizedTypes[0];
  const normalizedScale =
    Number.isFinite(scale) && scale > 0 ? Math.min(scale, 1) : 1;

  // Match the centered tab bar to the widest configured preview at this scale.
  const tabsMaxWidth =
    Math.max(
      ...normalizedTypes.map((type) => MEDIA_PREVIEW_CONFIG[type].width)
    ) * normalizedScale;

  // Build complete slides so preview content and optional metadata move together.
  const slides = normalizedContent.map((item, index) => (
    <div
      className='flex w-full flex-col items-center gap-6'
      data-testid='media-preview-slide'
      key={index}>
      <div className='flex w-full justify-center'>
        <MediaPreview
          type={activeType}
          scale={item.scale ?? scale}
          image={item.image}
          backgroundImage={item.backgroundImage}
          borderRadius={item.borderRadius ?? borderRadius}
        />
      </div>
      {renderContentMeta?.(item, index)}
    </div>
  ));

  return (
    <div
      className={`flex w-full min-w-0 flex-col items-center gap-6 ${className}`.trim()}
      data-testid='media-preview-wrapper'>
      {normalizedTypes.length > 1 && (
        <div
          className='flex w-full justify-center'
          data-testid='media-preview-tabs'
          style={{maxWidth: tabsMaxWidth}}>
          <TabsNavigation
            activeTabIndex={activeTypeIndex}
            className='w-full border-b border-[color:var(--color-border-divider)]'
            handleSetActiveTab={setActiveTypeIndex}>
            {normalizedTypes.map((type) => (
              <Tab key={type} label={MEDIA_PREVIEW_CONFIG[type].label} />
            ))}
          </TabsNavigation>
        </div>
      )}
      {normalizedContent.length > 1 ? (
        <CarouselV2 itemData={slides} showPagination />
      ) : (
        slides[0]
      )}
    </div>
  );
}

MediaPreviewWrapper.propTypes = {
  types: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  ),
  content: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.string,
      backgroundImage: PropTypes.string,
      scale: PropTypes.number,
      borderRadius: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    })
  ),
  scale: PropTypes.number,
  borderRadius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  renderContentMeta: PropTypes.func,
  className: PropTypes.string
};

export default React.memo(MediaPreviewWrapper);
